import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MdArrowBack, MdTimer, MdAccessTime } from 'react-icons/md';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { format, subDays, startOfMonth, startOfYear } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useLanguage } from '../../context/LanguageContext';

const CustomTooltip = ({ active, payload, label, unit }) => {
    if (active && payload && payload.length) {
        return (
            <div style={{
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                borderRadius: 10, padding: '10px 14px', fontSize: 13
            }}>
                <p style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</p>
                <p style={{ color: 'var(--primary)', fontWeight: 700, fontSize: 15 }}>
                    {payload[0].value} <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>{unit}</span>
                </p>
            </div>
        );
    }
    return null;
};

export default function CompanyCounterPage() {
    const { counterUUID } = useParams();
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState(format(subDays(new Date(), 1), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [currentCounter, setCurrentCounter] = useState(null);
    const [period, setPeriod] = useState('minute');
    const [activeRange, setActiveRange] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const fnsLocale = language === 'tr' ? tr : undefined;

    const getPeriodLabel = useCallback((p) => {
        switch (p) {
            case 'minute': return t('counterDetail.minute');
            case 'hour': return t('counterDetail.hourly');
            case 'day': return t('counterDetail.dailyPeriod');
            case 'week': return t('counterDetail.weeklyPeriod');
            case 'month': return t('counterDetail.monthlyPeriod');
            case 'year': return t('counterDetail.yearlyPeriod');
            default: return p;
        }
    }, [t]);

    const formatDateLabel = useCallback((timestamp, p) => {
        const date = new Date(timestamp);
        switch (p) {
            case 'minute': return format(date, 'HH:mm:ss', { locale: fnsLocale });
            case 'hour': return format(date, 'HH:mm', { locale: fnsLocale });
            case 'day': return format(date, 'dd MMM', { locale: fnsLocale });
            case 'week': return `${t('counterDetail.weekLabel')} ${format(date, 'w')}`;
            case 'month': return format(date, 'MMM yyyy', { locale: fnsLocale });
            case 'year': return format(date, 'yyyy', { locale: fnsLocale });
            default: return format(date, 'dd/MM HH:mm', { locale: fnsLocale });
        }
    }, [fnsLocale, t]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/counter-data/${counterUUID}`, {
                params: { startDate, endDate, period }
            });

            const rawData = res.data.data || [];
            const chartData = rawData.map(d => ({
                time: formatDateLabel(d.timestamp, period),
                value: d.value,
                fullTime: format(new Date(d.timestamp), 'yyyy/MM/dd HH:mm:ss', { locale: fnsLocale })
            }));
            setData(chartData);
            setCurrentPage(1);

            if (res.data.counter) {
                const c = res.data.counter;
                setCurrentCounter({
                    ...c,
                    systemName: c.systemId?.systemName || t('common.unknownSystem')
                });
            }
        } catch (err) {
            toast.error(t('sensorDetail.dataLoadFailed'));
        } finally {
            setLoading(false);
        }
    }, [counterUUID, startDate, endDate, period, formatDateLabel, fnsLocale, t]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const setQuickRange = (rangeType) => {
        const end = new Date();
        let start;
        let p = 'hour';

        switch (rangeType) {
            case 'day': start = subDays(end, 1); p = 'hour'; setActiveRange(1); break;
            case 'week': start = subDays(end, 7); p = 'day'; setActiveRange(7); break;
            case 'month': start = startOfMonth(end); p = 'day'; setActiveRange(30); break;
            case 'year': start = startOfYear(end); p = 'month'; setActiveRange(365); break;
            default: start = subDays(end, 7); p = 'hour';
        }
        
        setStartDate(format(start, 'yyyy-MM-dd'));
        setEndDate(format(end, 'yyyy-MM-dd'));
        setPeriod(p);
    };

    const totalConsumption = data.reduce((sum, item) => sum + item.value, 0);
    const averageConsumption = data.length > 0 ? (totalConsumption / data.length) : 0;

    return (
        <div className="page-wrapper">
            <div className="page-header">
                <div className="page-header-left">
                    <button className="btn btn-secondary btn-icon" onClick={() => navigate('/company')} style={{ marginRight: 8 }}>
                        <MdArrowBack />
                    </button>
                    <div>
                        <h1 className="page-title">
                            {currentCounter ? currentCounter.counterName : (loading ? t('counterDetail.counterLoading') : t('counterDetail.title'))}
                        </h1>
                        <p className="page-subtitle" style={{ fontSize: 13, marginBottom: 2 }}>
                            {currentCounter ? currentCounter.systemName : ''}
                        </p>
                    </div>
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: 'rgba(245,158,11,0.15)', color: 'var(--warning)' }}>
                        <MdTimer />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{averageConsumption.toFixed(2)} {currentCounter?.unit || ''}</div>
                        <div className="stat-label">{t('counterDetail.averageConsumption', { period: getPeriodLabel(period) })}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: 'rgba(16,185,129,0.15)', color: 'var(--success)' }}>
                        Σ
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{totalConsumption.toFixed(2)} {currentCounter?.unit || ''}</div>
                        <div className="stat-label">{t('counterDetail.periodConsumption')}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: 'rgba(79,142,247,0.15)', color: 'var(--primary)' }}>
                        <MdAccessTime />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value" style={{ textTransform: 'capitalize' }}>
                            {getPeriodLabel(period)}
                        </div>
                        <div className="stat-label">{t('counterDetail.queryPeriod')}</div>
                    </div>
                </div>
            </div>

            <div className="filter-bar">
                <div className="filter-bar-group">
                    <span className="filter-label" style={{ alignSelf: 'flex-end', paddingBottom: 6 }}>📅 {t('counterDetail.range')}:</span>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('sensorDetail.startDate')}</span>
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                    </label>
                    <span style={{ color: 'var(--text-muted)', paddingBottom: 6 }}>→</span>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('sensorDetail.endDate')}</span>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                    </label>

                    <div className="quick-range-group">
                        <button className={`btn btn-sm ${activeRange === 1 ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setQuickRange('day')}>{t('counterDetail.hours24')}</button>
                        <button className={`btn btn-sm ${activeRange === 7 ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setQuickRange('week')}>{t('counterDetail.days7')}</button>
                        <button className={`btn btn-sm ${activeRange === 30 ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setQuickRange('month')}>{t('counterDetail.thisMonth')}</button>
                    </div>
                </div>

                <div className="filter-bar-group-end">
                    <span className="filter-label">⏱️ {t('counterDetail.periodLabel')}:</span>
                    <select className="form-select form-select-sm" value={period} onChange={e => setPeriod(e.target.value)} style={{ width: 120 }}>
                        <option value="minute">{t('counterDetail.minute')}</option>
                        <option value="hour">{t('counterDetail.hourly')}</option>
                        <option value="day">{t('counterDetail.dailyPeriod')}</option>
                        <option value="week">{t('counterDetail.weeklyPeriod')}</option>
                        <option value="month">{t('counterDetail.monthlyPeriod')}</option>
                        <option value="year">{t('counterDetail.yearlyPeriod')}</option>
                    </select>
                    <button className="btn btn-secondary btn-sm" onClick={fetchData}>{t('common.filter')}</button>
                </div>
            </div>

            <motion.div className="card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <div className="card-header">
                    <div className="card-title">{t('counterDetail.consumptionChart')}</div>
                </div>
                <div className="card-body">
                    {loading ? (
                        <div className="loading-container"><span className="spinner"></span></div>
                    ) : (
                        <ResponsiveContainer width="100%" height={350}>
                            <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="counterGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                <XAxis dataKey="time" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                                <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickLine={false} axisLine={false} />
                                <Tooltip content={<CustomTooltip unit={currentCounter?.unit} />} />
                                <Area type="monotone" dataKey="value" stroke="#f59e0b" strokeWidth={2.5} fill="url(#counterGrad)"
                                    dot={data.length < 30 ? { fill: '#f59e0b', r: 3 } : false}
                                    activeDot={{ r: 5, fill: '#f59e0b', stroke: 'white', strokeWidth: 2 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </motion.div>

            {/* Data Table */}
            {data.length > 0 && (
                <motion.div className="card" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                    style={{ marginTop: 20 }}>
                    <div className="card-header">
                        <div className="card-title">{t('sensorDetail.recentRecords')}</div>
                        <span className="badge badge-primary">{data.length} {t('common.records')}</span>
                    </div>
                    <div className="table-container">
                        <table className="table">
                            <thead>
                                <tr><th>{t('sensorDetail.dateTime')}</th><th>{t('common.value')}</th></tr>
                            </thead>
                            <tbody>
                                {[...data].reverse()
                                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                    .map((d, i) => (
                                        <tr key={i}>
                                            <td style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 500 }}>{d.fullTime || d.time}</td>
                                            <td>
                                                <span style={{ fontWeight: 700, color: 'var(--primary)', fontSize: 20 }}>{d.value}</span>
                                                <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-muted)', marginLeft: 6 }}>{currentCounter?.unit}</span>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {data.length > itemsPerPage && (
                        <div className="pagination" style={{ display: 'flex', justifyContent: 'center', padding: '15px', gap: '8px', borderTop: '1px solid var(--border)' }}>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                {t('common.back')}
                            </button>
                            <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, color: 'var(--text-muted)', padding: '0 10px' }}>
                                {t('common.page')} {currentPage} / {Math.ceil(data.length / itemsPerPage)}
                            </div>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setCurrentPage(p => Math.min(Math.ceil(data.length / itemsPerPage), p + 1))}
                                disabled={currentPage === Math.ceil(data.length / itemsPerPage)}
                            >
                                {t('common.forward')}
                            </button>
                        </div>
                    )}
                </motion.div>
            )}
        </div>
    );
}
